<template lang="pug">
  div(v-if="isHouse(getPickupPropertyType)" :class="customClasses ? '' : 'mt-ds-sm lg:mt-ds-md'")
    VeeField(
      name="pickupPropertyType"
      v-slot="{ field, errors }"
    )
      SelRefined(
        v-bind="field"
        :value="getPickupPropertyType"
        @input="handlePropertyType"
        :options="getPropertyTypesHouse"
        option-text="name"
        option-value="value"
        :placeholder="$t('location_form.select_property_type')"
        :custom-classes="!errors.length && isFlat(getPickupPropertyType) ? '!rounded-b-none ' + customClasses : customClasses"
        :initial-open="true"
        :sub-menu="true"
        indicator
        qa-tag="sel_pickup_prop_type_house"
      )
      VeeErrorMessage(name="pickupPropertyType")
        TextInputError(class="my-ds-sm")
          | {{ $t('location_form.error.property_type_from') }}
  div(v-else-if="isFlat(getPickupPropertyType)" :class="customClasses ? '' : 'mt-ds-sm lg:mt-ds-md'")
    VeeField(
      name="pickupPropertyType"
      v-slot="{ field, errors }"
    )
      SelRefined(
        v-bind="field"
        :value="getPickupPropertyType"
        @input="handlePropertyType"
        :options="getPropertyTypesFlat"
        option-text="name"
        option-value="value"
        :custom-classes="!errors.length && !propertyFloorHidden && requiresFloor(getPickupPropertyType) ? '!rounded-b-none ' + customClasses : customClasses"
        :placeholder="$t('location_form.select_property_type')"
        :initial-open="true"
        :sub-menu="true"
        indicator
        qa-tag="sel_pickup_prop_type_flat"
      )
      VeeErrorMessage(name="pickupPropertyType")
        TextInputError(class="my-ds-sm")
          | {{ $t('location_form.error.property_type_from') }}

      SelRefined(
        v-if="!propertyFloorHidden && requiresFloor(getPickupPropertyType)"
        :value="getPickupFloor"
        @input="handlePickupFloor"
        :class="!errors.length ? '' : 'mt-ds-sm lg:mt-ds-md'"
        :custom-classes="!errors.length ? '!rounded-t-none !border-t-0 ' + floorStylingToConnectLiftAvailable  : floorStylingToConnectLiftAvailable"
        :options="getFloors"
        option-text="name"
        option-value="value"
        indicator
        qa-tag="sel_pickup_floor")

    SelRefined(
      v-if="requiresFloor(getPickupPropertyType) && getPickupFloor !== 'Ground floor'"
      :value="liftAvailable"
      @input="handleLiftAvailable"
      :custom-classes="'!rounded-t-none !border-t-0'"
      :options="getLiftAvailableOptions"
      option-text="name"
      option-value="value"
      indicator
      qa-tag="sel_pickup_lift")

  div(v-else-if="!propertyTypeHidden" :class="customClasses ? '' : 'mt-ds-sm lg:mt-ds-md'")
    VeeField(
      name="pickupPropertyType"
      v-slot="{ field, errors }"
    )
      SelRefined(
        v-bind="field"
        :value="getPickupPropertyType"
        @input="handlePropertyType"
        :options="getPropertyTypesMain"
        option-text="name"
        option-value="value"
        :custom-classes="!errors.length && !propertyFloorHidden && requiresFloor(getPickupPropertyType) ? '!rounded-b-none ' + customClasses : customClasses"
        :placeholder="$t('location_form.select_property_type')"
        :initial-open="getPickupPropertyType === ''"
        indicator
        qa-tag="sel_pickup_prop_type")
      VeeErrorMessage(name="pickupPropertyType")
        TextInputError(class="my-ds-sm")
          | {{ $t('location_form.error.property_type_from') }}

      SelRefined(
        v-if="!propertyFloorHidden && requiresFloor(getPickupPropertyType)"
        :value="getPickupFloor"
        @input="handlePickupFloor"
        :class="!errors.length ? '' : 'mt-ds-sm lg:mt-ds-md'"
        :custom-classes="!errors.length ? '!rounded-t-none !border-t-0 ' + floorStylingToConnectLiftAvailable  : floorStylingToConnectLiftAvailable"
        :options="getFloors"
        option-text="name"
        option-value="value"
        indicator
        qa-tag="sel_pickup_floor")

    SelRefined(
      v-if="requiresFloor(getPickupPropertyType) && getPickupFloor !== 'Ground floor'"
      :value="liftAvailable"
      @input="handleLiftAvailable"
      :custom-classes="'!rounded-t-none !border-t-0'"
      :options="getLiftAvailableOptions"
      option-text="name"
      option-value="value"
      indicator
      qa-tag="sel_pickup_lift")
</template>

<script lang="ts">
import { defineComponent } from "vue"
import { mapGetters, mapActions } from "vuex"
import { getAttributes } from "~/utils/block"
import {
  SET_PICKUP_PROPERTY_TYPE,
  SET_LISTING,
  SET_PICKUP_FLOOR,
  SET_PICKUP_LIFT_AVAILABLE,
  GET_PICKUP_FLOOR,
  GET_PICKUP_PROPERTY_TYPE,
  GET_PICKUP_LIFT_AVAILABLE,
  state as InitialListing
} from "~/store/listing"
import { isHouse, isFlat } from "~/services/public/Listing"
import {
  FLOOR_GROUND,
  FLOORS,
  LIFT_AVAILABLE,
  LIFT_UNAVAILABLE,
  PROPERTY_TYPE_LIST_FLAT,
  PROPERTY_TYPE_LIST_HOUSE,
  PROPERTY_TYPE_LIST_MAIN
} from "~/constants/listing"
import {
  GET_FLOORS,
  GET_PROPERTY_TYPES,
  GET_PROPERTY_TYPES_MAIN,
  GET_PROPERTY_TYPES_FLAT,
  GET_PROPERTY_TYPES_HOUSE
} from "~/store/property"
import { swapFirstTwoElements } from "~/scripts/useful-functions"
import { LOCALE } from "~/constants/locale"

interface ComponentData {
  propertyTypesMain: any[]
  propertyTypesHouse: any[]
  propertyTypesFlat: any[]
  floors: any[]
}

export default defineComponent({
  name: "PropertyPickupInput",

  props: {
    propertyTypeHidden: {
      type: Boolean
    },
    propertyFloorHidden: {
      type: Boolean
    },
    block: {
      type: Object as () => Block,
      required: true
    },
    virgin: {
      type: Boolean
    },
    lazy: {
      type: Boolean,
      default: true
    },
    validation: {
      type: Boolean
    },
    customClasses: {
      type: String
    }
  },

  data(): ComponentData {
    return {
      propertyTypesMain: PROPERTY_TYPE_LIST_MAIN,
      propertyTypesHouse: PROPERTY_TYPE_LIST_HOUSE,
      propertyTypesFlat: PROPERTY_TYPE_LIST_FLAT,
      floors: FLOORS
    }
  },

  computed: {
    ...mapGetters("listing", {
      getPickupPropertyType: GET_PICKUP_PROPERTY_TYPE,
      getPickupFloor: GET_PICKUP_FLOOR,
      getPickupLiftAvailable: GET_PICKUP_LIFT_AVAILABLE
    }),
    ...mapGetters("property", {
      getStorePropertyTypes: GET_PROPERTY_TYPES,
      getStorePropertyTypesMain: GET_PROPERTY_TYPES_MAIN,
      getStorePropertyTypesHouse: GET_PROPERTY_TYPES_HOUSE,
      getStorePropertyTypesFlat: GET_PROPERTY_TYPES_FLAT,
      getStoreFloors: GET_FLOORS
    }),

    getPropertyTypesMain(): any[] {
      if (!this.getStorePropertyTypesMain) return this.propertyTypesMain

      const propertyTypesToShow = this.getStorePropertyTypesMain.filter(type => !type.hidden)

      if (this.$i18n.locale === LOCALE.ES) {
        /* Spain-only experiment where we invert the order of house and flat to test a hypothesis */
        return swapFirstTwoElements(propertyTypesToShow)
      }
      return propertyTypesToShow
    },

    getPropertyTypesHouse(): any[] {
      return this.getStorePropertyTypesHouse || this.propertyTypesHouse
    },

    getPropertyTypesFlat(): any[] {
      return this.getStorePropertyTypesFlat || this.propertyTypesFlat
    },

    getFloors(): any[] {
      return this.getStoreFloors || this.floors
    },

    attributes(): any {
      return this.virgin ? null : this.getAttributes(this.block)
    },

    liftAvailable(): number {
      if (this.getPickupLiftAvailable === null) {
        this.setLiftAvailable(LIFT_AVAILABLE)
        return LIFT_AVAILABLE
      }
      return this.getPickupLiftAvailable
    },

    getLiftAvailableOptions(): any[] {
      return [
        {
          name: this.$t("location_form.lift_available"),
          value: LIFT_AVAILABLE
        },
        {
          name: this.$t("location_form.lift_not_available"),
          value: LIFT_UNAVAILABLE
        }
      ]
    },
    floorStylingToConnectLiftAvailable(): string {
      return this.getPickupFloor !== 'Ground floor' ? '!rounded-b-none' : ''
    }
  },

  created() {
    this.setListing(InitialListing())
  },

  methods: {
    ...mapActions("listing", {
      setPickupFloor: SET_PICKUP_FLOOR,
      setListing: SET_LISTING,
      setPickupPropertyType: SET_PICKUP_PROPERTY_TYPE,
      setLiftAvailable: SET_PICKUP_LIFT_AVAILABLE
    }),

    handlePropertyType(value: string) {
      if (!this.isFlat(value as string) || this.getPickupFloor === FLOOR_GROUND) {
        this.setPickupFloor(FLOOR_GROUND)
        this.setLiftAvailable(null)
      }
      this.setPickupPropertyType(value)
    },

    handlePickupFloor(value: string | null): void {
      if (value === FLOOR_GROUND) {
        this.setLiftAvailable(null)
      }
      this.setPickupFloor(value)
    },

    handleLiftAvailable(value: string | null): void {
      this.setLiftAvailable(value)
    },

    getAttributes,

    isHouse(propertyType: string): boolean {
      return isHouse(propertyType) as boolean
    },

    isFlat(propertyType: string): boolean {
      return isFlat(propertyType) as boolean
    },

    requiresFloor(propertyType: string): boolean {
      if (!this.getStorePropertyTypes) {
        return this.propertyTypeHidden && !this.propertyFloorHidden
      }
      const propertyTypeObject: undefined | ListingPropertyType = this.getStorePropertyTypes.find(
        storePropertyType => storePropertyType.value === propertyType
      )
      return !!propertyTypeObject?.propertyWithLevels
    }
  }
})
</script>

<style lang="css" module></style>
